import React from "react";
import {toVND} from "../../../helpers/helpers";
import { toPng } from "html-to-image";

interface OrderSuccessProps {
    orderState: any;
    createdOrderId: string | null;
    onBackToMenu: () => void;
}

const groupOptions = (options: any[] = []) => {
    return options.reduce((acc: Record<string, any[]>, cur) => {
        const key = cur.option_name_snapshot ?? cur.option_name ?? "Tùy chọn";
        if (!acc[key]) acc[key] = [];
        acc[key].push(cur);
        return acc;
    }, {});
};

const renderOrderItemOptions = (options: any[] = []) => {
    if (!options.length) return null;

    const groups = groupOptions(options);

    return (
        <div className="mt-1 small text-muted">
            {Object.entries(groups).map(([groupName, items]) => (
                <div key={groupName} className="mb-1">
                    <div className="fw-medium">{groupName}:</div>

                    {items.map((opt: any, idx: number) => (
                        <div key={idx} className="d-flex justify-content-between ps-2">
                            <span>• {opt.option_value_label_snapshot ?? opt.name}</span>

                            {Number(opt.price_modifier_snapshot ?? opt.price) > 0 && (
                                <span>+{toVND(Number(opt.price_modifier_snapshot ?? opt.price))}</span>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

const OrderSuccess: React.FC<OrderSuccessProps> = ({
                                                       orderState,
                                                       createdOrderId,
                                                       onBackToMenu,
                                                   }) => {
    const order = orderState?.order;
    const orderItems = order?.order_items ?? order?.items ?? [];
    const total = order?.total ?? order?.total_price ?? 0;

    const downloadOrderAsImage = async () => {
        const node = document.getElementById("order-success");
        if (!node) return;

        try {
            const dataUrl = await toPng(node, {
                backgroundColor: "#ffffff", // nền trắng
                cacheBust: true,
                pixelRatio: 2, // ảnh nét hơn
            });

            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = `don-hang-${order?.order_number ?? createdOrderId}.png`;
            link.click();
        } catch (err) {
            console.error("Export order image failed", err);
        }
    };

    return (
        <div className="container py-3" id="order-success">
            <div className="py-3 text-center border-dashed-top-bottom">
                <h5 className="text-uppercase">Mã Đơn Hàng</h5>
                <p className="text-dark fw-bold display-1">{order?.order_number ?? createdOrderId}</p>
            </div>
            <div className="py-3 border-dashed-bottom">
                {/* Tiêu đề */}
                <h6 className="text-center mb-3">Thông tin đơn hàng</h6>

                {/* Danh sách món */}
                <div className="px-2">
                    {orderState?.loading?.getOrder && (
                        <div className="text-center fst-italic text-muted py-2">
                            Đang tải thông tin đơn hàng…
                        </div>
                    )}

                    {!orderState?.loading?.getOrder && orderItems.length === 0 && (
                        <p className="text-center fst-italic text-muted mb-2">
                            Chưa có món trong đơn.
                        </p>
                    )}

                    {!orderState?.loading?.getOrder && orderItems.length > 0 && (
                        <ul className="list-unstyled mb-2">
                            {orderItems.map((item: any, index: number) => {
                                const qty = Number(item.quantity || 0);
                                const base = Number(item.base_price ?? item.unit_price ?? 0);
                                const lineTotal = Number(item.line_total || 0);

                                return (
                                    <li key={item.id ?? `${item.name}-${index}`} className="py-2 border-bottom">
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <div>{item.name}</div>
                                                <small className="fst-italic text-muted">
                                                    {qty} × {toVND(base)}
                                                </small>
                                            </div>
                                            <div className="fw-medium">
                                                {toVND(lineTotal)}
                                            </div>
                                        </div>

                                        {renderOrderItemOptions(item.option_values ?? item.options)}

                                        {item.note && (
                                            <div className="small fst-italic text-muted mt-1 ps-2 border-start">
                                                Ghi chú: {item.note}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {orderState?.success?.getOrder && (
                        <div className="d-flex justify-content-between pt-2 mt-2 border-top">
                            <span className="fw-medium">Tổng cộng</span>
                            <span className="fw-medium">
                    {toVND(Number(total))}
                </span>
                        </div>
                    )}
                </div>

                {/* Lưu ý */}
                <p className="text-center mt-3 mb-0">
                    <span className="fst-italic">
                        Lưu ý: Đơn hàng của bạn đã được tiếp nhận. Vui lòng chờ trong giây lát,
                        nhân viên sẽ đến xác nhận.
                    </span>
                </p>
            </div>
            <div className="text-center mt-3 d-flex gap-2 justify-content-center">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={downloadOrderAsImage}
                >
                    Lưu hóa đơn
                </button>
                <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={onBackToMenu}
                >
                    Quay lại thực đơn
                </button>
            </div>
        </div>
    );
};

export default OrderSuccess;
